import { apiClient, ApiResponse, falveyClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { ToastType } from '@components/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { carrierDetailsStore } from '@store/carriers/carrierDetails/store';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  CarrierLiabilityTypeViewModel,
  CarrierMaxLiability,
  FalveyInsuranceQuote,
  FalveyQuotesPayload,
  GetCalculationsFromLoadItemsApiResponse,
  LoadItemCalculationInput,
  LoadItemCalculationTotals,
  LTLPricingRequestType,
  LTLReferenceNumber,
  LTLTrackingData,
  QuoteParametersV2ApiResponse,
  QuoteParametersV2Params,
  QuoteParametersV2Response,
  QuoteSpecifics,
} from '@store/ltl/types';
import { fetchLtlAccessorialCatalog } from '@store/orders/services';
import {
  LtlQuoteParametersViewModel,
  LtlQuoteResultsViewModel,
  LtlQuoteViewModel,
} from '@store/orders/types';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import { ItemAccessorial } from '@views/ltl/types';

function hasValueArrayProperty(
  obj: unknown,
): obj is { value: LtlQuoteViewModel[] } {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  const potentiallyHasValue = obj as { value?: unknown };

  return Array.isArray(potentiallyHasValue.value);
}

const ltlRateFieldLabels: Record<string, string> = {
  quantity: '# of Units',
  item: 'Unit Type',
  description: 'Item Description',
  pieces: '# of Pcs',
  weight: 'Weight',
  lengthInch: 'Length',
  widthInch: 'Width',
  heightInch: 'Height',
  nmfc: 'NMFC',
  class: 'Class',
  hazmat: 'Hazmat',
};

const getFriendlyLtlRatesErrorMessage = (error: unknown) => {
  const fallbackError = createError(error, 'Failed to get rates');
  let rawMessage = fallbackError.message;

  if (typeof error === 'string') {
    rawMessage = error;
  } else if (error instanceof Error) {
    rawMessage = error.message;
  }

  const subItemPathPattern =
    /Path 'loadItems\[(\d+)\]\.quoteLoadItemBreakdown\[(\d+)\]\.(\w+)'/;
  const subItemPathMatch = subItemPathPattern.exec(rawMessage);

  if (subItemPathMatch) {
    const [, loadItemIndex, subItemIndex, fieldName] = subItemPathMatch;
    const fieldLabel = ltlRateFieldLabels[fieldName] ?? fieldName;
    const loadItemLabel = `Load item ${Number(loadItemIndex) + 1}`;
    return `Not all required fields are filled out. Check ${loadItemLabel}, sub-item ${Number(subItemIndex) + 1}: ${fieldLabel}.`;
  }

  const itemPathPattern = /Path 'loadItems\[(\d+)\]\.(\w+)'/;
  const itemPathMatch = itemPathPattern.exec(rawMessage);

  if (!itemPathMatch) {
    return fallbackError.message;
  }

  const [, loadItemIndex, fieldName] = itemPathMatch;
  const fieldLabel = ltlRateFieldLabels[fieldName] ?? fieldName;
  const loadItemLabel = `Load item ${Number(loadItemIndex) + 1}`;
  return `Not all required fields are filled out. Check ${loadItemLabel}: ${fieldLabel}.`;
};

// Always resolves accessorials from the legacy GUID-keyed endpoint. Some
// write flows (e.g. saving a customer address's accessorials via
// PUT /api/CustomerAddress) still require a legacy Accessorial GUID and have
// not been migrated to accept the new schema's numeric ltlAccessorialId, so
// they must use this directly instead of the flag-aware getLTLAccessorials().
export async function fetchLegacyLtlAccessorials(): Promise<
  ItemAccessorial[] | undefined
> {
  const response = await apiClient.get('/order/getAccessorial');
  if ('value' in response) {
    return response.value as ItemAccessorial[];
  }
}

export async function getLTLAccessorials(): Promise<
  ItemAccessorial[] | undefined
> {
  if (isFeatureFlagEnabled(FeatureFlags.LtlAccessorialsSchemaRefactorRead)()) {
    const catalog = await fetchLtlAccessorialCatalog();
    return catalog
      .filter((item) => item.displayInSelector)
      .map((item) => {
        const typeId = item.accessorialTypeId;
        const isPickup = typeId === 1;
        const isDelivery = typeId === 2;
        return {
          id: String(item.id),
          name: item.displayName,
          isGlobal: !isPickup && !isDelivery,
          isDeleted: false,
          sortOrder: item.displayOrder ?? undefined,
          isDelivery,
          isPickup,
        } satisfies ItemAccessorial;
      });
  }
  return fetchLegacyLtlAccessorials();
}
export async function getTempQuote(request: string) {
  try {
    const response = await apiClient.get<QuoteSpecifics>(
      `/ltl/getQuote/${request}`,
    );
    if ('value' in response) {
      return response.value;
    }
  } catch (error: unknown) {
    logError('getTempQuote', error as Error);

    handleToast(ToastType.Error, 'Failed to Load Quote Information');
    throw createError(error, 'Failed to load quote information');
  }
}

export async function getDefaultAddress(customerId: number) {
  try {
    const response = await apiClient.get(
      `/CustomerAddress/getdefaultaddress/${customerId}`,
    );
    return response;
  } catch (error: unknown) {
    const err = createError(error, 'Failed to get default address');
    handleToast(ToastType.Error, err.message);
  }
}

export async function GetCalculationsFromLoadItems(
  request: LoadItemCalculationInput,
): Promise<LoadItemCalculationTotals> {
  try {
    const response: GetCalculationsFromLoadItemsApiResponse =
      await apiClient.post('/LTL/v2/LoadItemCalculations', request);

    if (response.isSuccess) return response.value;

    throw new Error('Failed to calculate Load Items');
  } catch (error: unknown) {
    throw new Error(`Failed to calculate Load Items: ${error}`);
  }
}

export async function fetchQuoteParameters(
  isHistoryRequest: boolean,
  customerId?: number,
): Promise<LtlQuoteParametersViewModel[]> {
  try {
    const response = await apiClient.get<{
      value?: LtlQuoteParametersViewModel[];
    }>(
      `/Ltl/QuoteParameters?isHistoryRequest=${isHistoryRequest}${
        Boolean(customerId) ? `&customerId=${customerId}` : ''
      }`,
    );

    if (Array.isArray(response)) {
      return response as LtlQuoteParametersViewModel[];
    }
    throw new Error('Invalid response format: Expected an array in "value".');
  } catch (error: unknown) {
    logError(`fetchLtlQuoteParameters(${isHistoryRequest})`, error as Error);
    throw new Error(
      `Failed to fetch LTL Quote Parameters (isHistoryRequest=${isHistoryRequest}): ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
}

export async function fetchQuoteParametersV2(
  params: QuoteParametersV2Params,
): Promise<QuoteParametersV2Response> {
  try {
    const queryParams = new URLSearchParams({
      isHistoryRequest: String(params.isHistoryRequest),
      pageNumber: String(params.pageNumber),
      pageSize: String(params.pageSize),
    });

    const optionalParams: Record<string, string | number | undefined> = {
      customerId: params.customerId,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      filterCustomerName: params.filterCustomerName,
      filterQuoteRequestLocator: params.filterQuoteRequestLocator,
      filterDateSavedFrom: params.filterDateSavedFrom,
      filterDateSavedTo: params.filterDateSavedTo,
      filterExpDateFrom: params.filterExpDateFrom,
      filterExpDateTo: params.filterExpDateTo,
      filterOriginPostal: params.filterOriginPostal,
      filterDestPostal: params.filterDestPostal,
      filterCreatedByName: params.filterCreatedByName,
    };

    for (const [key, value] of Object.entries(optionalParams)) {
      if (value != null) {
        queryParams.set(key, String(value));
      }
    }

    const response: V4ApiResponse<QuoteParametersV2ApiResponse> =
      await apiClient.get(`/ltl/v2/QuoteParameters?${queryParams.toString()}`);

    if (response.isSuccess) {
      return {
        data: response.value.quoteParameters,
        totalCount: response.value.totalRecordsCount,
      };
    }
    throw new Error('Failed to fetch quote parameters v2');
  } catch (error: unknown) {
    const err = createError(error, 'Failed to fetch quotes');
    throw handleToast(ToastType.Error, err.message);
  }
}

export async function fetchQuoteParametersByQuoteId(
  quoteId: number,
  getFullQuote?: boolean,
): Promise<LtlQuoteParametersViewModel> {
  getFullQuote ??= false;

  try {
    const response = await apiClient.get<{
      value?: LtlQuoteParametersViewModel;
    }>(
      `/Ltl/GetQuoteParametersByQuoteId/${quoteId}?getFullQuote=${getFullQuote}`,
    );

    if ('value' in response) {
      return response.value as LtlQuoteParametersViewModel;
    }
    throw new Error(
      'Invalid response format: cannot get value of Quote from response.',
    );
  } catch (error: unknown) {
    logError('GetQuoteParametersByquoteId', error as Error);
    throw new Error(
      `Failed to fetch LTL Quote Parameters for quote ${quoteId}: ${String(
        error,
      )}`,
    );
  }
}

export async function fetchQuoteById(
  id: number,
  getFullQuote?: boolean,
): Promise<LtlQuoteViewModel | null> {
  getFullQuote ??= false;

  try {
    const response = await apiClient.get<{
      value?: LtlQuoteViewModel;
    }>(`/Ltl/GetLtlQuoteById/${id}?getFullQuote=${getFullQuote}`);

    if ('value' in response) {
      return response.value as LtlQuoteViewModel;
    }
    throw new Error(
      'Invalid response format: cannot get value of Quote from response.',
    );
  } catch (error: unknown) {
    logError(
      'GetLtlQuoteById',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch LTL Quote for Id ${id}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function fetchQuote(
  quoteParametersId: number,
): Promise<LtlQuoteViewModel[]> {
  try {
    const response = await apiClient.get<{
      value?: LtlQuoteViewModel[];
    }>(`/Ltl/Quote?QuoteParametersId=${quoteParametersId}`);

    if (hasValueArrayProperty(response)) {
      return response.value;
    }

    throw new Error('Invalid response format: Expected an array in "value".');
  } catch (error: unknown) {
    logError(`fetchQuote(${quoteParametersId})`, error as Error);
    throw new Error(
      `Failed to fetch LTL Quote (quoteParametersId=${quoteParametersId}): ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
}

export async function getFalveyExcludedtItems() {
  try {
    const response = await falveyClient.get('FalveyInsurance/getexcludeditems');
    return response;
  } catch (error: unknown) {
    logError('getFalveyExcludedItems', error as Error);
    throw createError(error, 'Failed to get excluded items');
  }
}

export const getFalveyPricingQuoteData = async (
  payload: FalveyQuotesPayload,
  hideToastr?: boolean,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response = await falveyClient.post(
      'falveyInsurance/getinsurancepricingquote',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    Boolean(hideToastr) &&
      handleToast(ToastType.Success, 'Get Falvey pricing quotes successfully');
    if ('value' in response) {
      return response.value as FalveyInsuranceQuote;
    }
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to get Falvey pricing quotes');
    logError('getFalveyPricingQuoteData', error as Error);
    throw createError(error, 'Failed to get Falvey pricing quotes');
  }
};

export async function fetchLtlReferenceNumbers(loadId: number) {
  try {
    const response = await apiClient.get<LTLTrackingData>(
      `/Ltl/GetReferenceNumbers/${loadId}`,
    );

    if ('value' in response) {
      return response.value;
    }
  } catch (error: unknown) {
    logError('getLtlReferenceNumbers', error as Error);
    throw new Error(`Failed to fetch LTL Reference Numbers: ${error}`);
  }
}

export async function LtlReferenceNumberUpdate(
  loadId: number,
  referenceNumbers: LTLReferenceNumber[],
  changeTrackingSource?: ChangeTrackingSource,
) {
  if (referenceNumbers.length === 0) {
    return;
  }
  try {
    const response: {
      isSuccess: boolean;
      message: string;
    } = await apiClient.post(
      `/LTL/ReferenceNumbers/${loadId}`,
      referenceNumbers,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return response.isSuccess;
  } catch (error: unknown) {
    logError('LtlReferenceNumberUpdate', error as Error);
    throw createError(error, 'Failed to update LTL shipment data');
  }
}

export async function fetchCarrierMaxLiability(
  carrierId: number,
  pricingRequestType: LTLPricingRequestType,
  customerId?: number | null,
) {
  try {
    const response = await apiClient.get<CarrierMaxLiability[]>(
      `/Ltl/CarrierMaxLiability?carrierId=${carrierId}&pricingRequestType=${pricingRequestType}${
        customerId != null && !isNaN(customerId)
          ? '&customerId=' + customerId
          : ''
      }`,
    );

    if ('value' in response) {
      return response.value as CarrierMaxLiability[];
    }
    return [];
  } catch (error: unknown) {
    logError('fetchCarrierMaxLiability', error as Error);
    throw new Error(`Failed to fetch Carrier Max Liability: ${error}`);
  }
}

/**
 * Upserts carrier max liability rows. The API accepts new rows (id 0) and
 * existing rows in the same PUT; POST on this route is no longer supported.
 * `carrierId` defaults to the loaded carrier and is passed explicitly when a
 * carrier has just been created.
 */
export async function putCarrierMaxLiability(
  carrierMaxLiabilities: CarrierMaxLiability[],
  changeTrackingSource?: ChangeTrackingSource,
  carrierId: number | undefined = carrierDetailsStore.carrierDetails?.id,
): Promise<void> {
  try {
    const updatedCarrierMaxLiabilities = carrierMaxLiabilities.map(
      (liability) => ({
        ...liability,
        carrierId,
      }),
    );
    const response = await apiClient.put(
      '/Ltl/CarrierMaxLiability',
      updatedCarrierMaxLiabilities,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );

    handleToast(ToastType.Success, response as unknown as string);
  } catch (error: unknown) {
    logError('putCarrierMaxLiability', error as Error);
    throw createError(error, 'Failed to update carrier liabilities');
  }
}

export async function fetchCarrierLiabilityTypes(carrierId: number) {
  try {
    const response = await apiClient.get<{
      value?: CarrierLiabilityTypeViewModel;
    }>(`/Ltl/CarrierLiabilityTypes/${carrierId}`);

    if ('value' in response) {
      return response.value;
    }

    return [];
  } catch (error: unknown) {
    logError('fetchCarrierLiabilityTypes', error as Error);
    throw new Error(`Failed to fetch Carrier Liability Types: ${error}`);
  }
}

export const fetchQuoteParametersByQuoteParameterId = async (
  quoteParametersId: number,
) => {
  try {
    const response: V4ApiResponse<LtlQuoteParametersViewModel> =
      await apiClient.get(`/Ltl/quoteParameters/${quoteParametersId}`);
    if ('value' in response) {
      return response.value;
    }
  } catch (error: unknown) {
    const e = createError(error, 'Failed to get quote parameters');
    handleToast(ToastType.Error, e.message);
  }
};

export const getLTLRates = async (
  quoteParameters: LtlQuoteParametersViewModel,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<LtlQuoteParametersViewModel> =
      await apiClient.put(
        '/ltl/Rate',
        quoteParameters,
        buildChangeTrackingRequestConfig(
          changeTrackingSource ? [changeTrackingSource] : [],
        ),
      );
    return response;
  } catch (error) {
    handleToast(ToastType.Error, getFriendlyLtlRatesErrorMessage(error));
  }
};

export const fetchQuoteResults = async (
  quoteParametersId: number,
  sinceSeq?: number,
): Promise<LtlQuoteResultsViewModel | undefined> => {
  try {
    const url =
      sinceSeq !== undefined
        ? `/ltl/quoteParameters/${quoteParametersId}/results?sinceSeq=${sinceSeq}`
        : `/ltl/quoteParameters/${quoteParametersId}/results`;

    const response: V4ApiResponse<LtlQuoteResultsViewModel> =
      await apiClient.get(url);

    if ('value' in response) {
      return response.value;
    }
  } catch (error) {
    const e = createError(error, 'Failed to fetch quote results');
    handleToast(ToastType.Error, e.message);
  }
};

export async function deleteFalveyInsurance(
  loadId: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.loadDetailsInsurance,
) {
  try {
    const response = await apiClient.delete<ApiResponse<boolean>>(
      `ltl/insurancepolicy/${loadId}`,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    if ('value' in response) {
      handleToast(ToastType.Success, 'Falvey insurance deleted successfully');
      return response.value as boolean;
    }
    return false;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to delete Falvey insurance');
    handleToast(ToastType.Error, error.message);
    return false;
  }
}
